import { useEffect, useMemo, useState } from "react";
import { useProFormaSocketContext } from "../../../socketContext/ProFormaSocketProvider";
import { RecoverableExpense, RecoverableExpenses } from "./types";
import RecoverableExpenseItem from "./RecoverableExpenseItem";
import { NSModal } from "bricks";
import './RecoverableExpensesModal.scss';
import { buildDelta, initFromExpenses } from "./helpers";

interface IRecoverableExpensesModalProps {
    isOpen: boolean;
    toggle: () => void;
}


const RecoverableExpensesModal = ({ isOpen, toggle }: IRecoverableExpensesModalProps) => {
    const {
        expenses,
        handleUpdateRecoverableExpenses,
    } = useProFormaSocketContext();

    const [recoverableExpenses, setRecoverableExpenses] = useState<RecoverableExpenses>(initFromExpenses(expenses));

    const handleUpdateRecoverableExpenseRow = (id: number, patch: Partial<RecoverableExpense>) => {
        setRecoverableExpenses(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    };

    const handleSelectAll = () => {
        setRecoverableExpenses(prev => Object.fromEntries(
            Object.keys(prev).map(id => [id, { isRecoverable: true, recoverablePercentage: 1 }]),
        ));
    };

    const handleResetToDefault = () => {
        setRecoverableExpenses(prev => Object.fromEntries(
            Object.keys(prev).map(id => [id, { isRecoverable: false, recoverablePercentage: 0 }]),
        ));
    };

    const handleSave = () => {                                                   
        const payload = buildDelta(recoverableExpenses, allExpenses);                           
        if (payload.length === 0) { toggle(); return; }
        handleUpdateRecoverableExpenses(payload, toggle);                        
    };

    useEffect(() => {
        if (isOpen) setRecoverableExpenses(initFromExpenses(expenses));     
        // Depend on [isOpen] only — initialize local state when the modal transitions to open, not on every expenses change.
        // eslint-disable-next-line react-hooks/exhaustive-deps                   
    }, [isOpen]);     
    
    const allExpenses = useMemo(() => [
        ...(expenses.operatingExpenses ?? []),
        ...(expenses.propertyTaxesOperatingExpense ?
            [expenses.propertyTaxesOperatingExpense] : []),
    ], [expenses]);

    return (
        <NSModal
            isOpen={isOpen}
            toggle={toggle}
            modalHeaderText="Recoverable expenses"
            modalFooterButtonText="Update recoverables"
            modalFooterButtonFunction={handleSave}
            modalFooterCancelButtonText="Cancel"
            modalFooterCancelButtonFunction={toggle}
            modalHeaderClassName="bg-primary modal-colored-header rounded"
            size="500px"
        >
            <div className="RecoverableExpensesModal__bulk-actions d-flex justify-content-end mb-2">
                <span
                    className="RecoverableExpensesModal__bulk-action text-primary text-uppercase small font-weight-bold cursor--pointer mr-3"
                    role="button"
                    tabIndex={0}
                    onClick={handleSelectAll}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleSelectAll(); }}
                >
                    Select all
                </span>
                <span
                    className="RecoverableExpensesModal__bulk-action text-primary text-uppercase small font-weight-bold cursor--pointer"
                    role="button"
                    tabIndex={0}
                    onClick={handleResetToDefault}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleResetToDefault(); }}
                >
                    Reset to default
                </span>
            </div>
            {allExpenses.map(exp => (
                <RecoverableExpenseItem
                    key={exp.id}
                    expense={exp}
                    recoverableExpense={recoverableExpenses[exp.id]}
                    updateRecoverableExpenseRow={handleUpdateRecoverableExpenseRow}
                />
            ))}
        </NSModal>
    );
}

export default RecoverableExpensesModal;

